import React, { FC } from 'react';
import { gqlTalentProfileUnion, gqlPublicCredit, gqlOrganization } from '@minecraft.graphql-types';
import { MediaType } from '../SummaryTypes';
import { SummaryPrimaryPhotoWrapper, SummaryInfoItem, SummaryInfoItemInner } from '../SummaryStyles';
import SummaryPrimaryPhoto from '../SummaryPrimaryPhoto';
import SummaryProfileInfo from '../SummaryProfileInfo';
import SummarySubmissionNote from '../SummarySubmissionNote';
import SummarySubmissionMedia from '../SummarySubmissionMedia';
import SummaryAbout from '../SummaryCommon';
import SummaryPrimaryWorkingLocation from '../SummaryPrimaryWorkingLocation';
import SummaryMeasurements from '../SummaryMeasurements';
import SummaryCredits from '../SummaryCredits';
import SummaryReels from '../SummaryReels';
import {
  StyledSummaryContainer,
  SummaryTopContent,
  SummaryBottomContent,
  SummaryBottomContentItem,
  StyledSummaryInfo,
} from './SummarySubmissionDetailsViewStyles';

type Props = {
  showPhoto: boolean;
  isPersonal: boolean;
  profileMainOrganization: gqlOrganization;
  profileMainDivision: gqlOrganization;
  stageName: string;
  primaryPhoto: MediaType;
  talentUnions: ReadonlyArray<gqlTalentProfileUnion>;
  submissionNote: string;
  submissionRequestedMedia: ReadonlyArray<MediaType>;
  blurb: string;
  primaryWorkingLocation: string;
  height: string;
  weight: string;
  credits: ReadonlyArray<gqlPublicCredit>;
  reels: ReadonlyArray<MediaType>;
  profileGuid: string;
  profileRoleId: number;
};

const SummarySubmissionDetailsView: FC<Props> = ({
  showPhoto,
  isPersonal,
  profileMainOrganization,
  profileMainDivision,
  stageName,
  primaryPhoto,
  talentUnions,
  submissionNote,
  submissionRequestedMedia,
  blurb,
  primaryWorkingLocation,
  height,
  weight,
  credits,
  reels,
  profileGuid,
  profileRoleId,
}) => {
  const isSummaryBottomContentVisible =
    !!blurb || !!primaryWorkingLocation || !!height || !!weight || credits?.length > 0 || reels?.length > 0;

  return (
    <StyledSummaryContainer>
      <SummaryTopContent>
        <SummaryPrimaryPhotoWrapper>
          {showPhoto && <SummaryPrimaryPhoto primaryPhoto={primaryPhoto} />}
        </SummaryPrimaryPhotoWrapper>
        <StyledSummaryInfo>
          <SummaryInfoItem>
            <SummaryProfileInfo
              isPersonal={isPersonal}
              stageName={stageName}
              talentUnions={talentUnions}
              profileMainOrganization={profileMainOrganization}
              profileMainDivision={profileMainDivision}
            />
          </SummaryInfoItem>
          {!!submissionNote && (
            <SummaryInfoItem>
              <SummarySubmissionNote note={submissionNote} />
            </SummaryInfoItem>
          )}
          {submissionRequestedMedia?.length > 0 && (
            <SummaryInfoItem>
              <SummarySubmissionMedia
                medias={submissionRequestedMedia}
                profileGuid={profileGuid}
                profileRoleId={profileRoleId}
              />
            </SummaryInfoItem>
          )}
        </StyledSummaryInfo>
      </SummaryTopContent>
      {isSummaryBottomContentVisible && (
        <SummaryBottomContent>
          <SummaryBottomContentItem>
            {!!blurb && (
              <SummaryInfoItem>
                <SummaryAbout text={blurb} />
              </SummaryInfoItem>
            )}
            {(!!primaryWorkingLocation || !!height || !!weight) && (
              <SummaryInfoItem>
                <SummaryInfoItemInner>
                  <SummaryPrimaryWorkingLocation primaryWorkingLocation={primaryWorkingLocation} />
                  <SummaryMeasurements height={height} weight={weight} />
                </SummaryInfoItemInner>
              </SummaryInfoItem>
            )}
          </SummaryBottomContentItem>
          {credits?.length > 0 && (
            <SummaryBottomContentItem>
              <SummaryCredits creditList={credits} />
            </SummaryBottomContentItem>
          )}
          {reels?.length > 0 && (
            <SummaryBottomContentItem>
              <SummaryReels reels={reels} />
            </SummaryBottomContentItem>
          )}
        </SummaryBottomContent>
      )}
    </StyledSummaryContainer>
  );
};

export default SummarySubmissionDetailsView;
